import { formatearMoneda } from '@plataforma/utils';

const CLAVE_STORAGE = 'plataforma-saas:sitio-publico:carrito';
const CLAVE_CUPON = 'plataforma-saas:sitio-publico:cupon';
const EVENTO_CAMBIO = 'tienda:carrito-cambio';

function leerCrudo() {
  try {
    const valor = JSON.parse(window.localStorage.getItem(CLAVE_STORAGE) ?? '[]');
    return Array.isArray(valor) ? valor : [];
  } catch {
    return [];
  }
}

/**
 * @param {{ itemKey: string, slug: string, cantidad: number, varianteId?: string|null, atributosSeleccionados?: Record<string, string>|null }[]} items
 */
function guardar(items) {
  if (typeof window !== 'undefined' && window.localStorage) {
    window.localStorage.setItem(CLAVE_STORAGE, JSON.stringify(items));
    window.dispatchEvent(new CustomEvent(EVENTO_CAMBIO));
  }
}

function normalizarCantidad(cantidad) {
  return Math.max(0, Math.trunc(Number(cantidad) || 0));
}

/**
 * @param {Record<string, string>|null|undefined} atributos
 */
function normalizarAtributos(atributos) {
  if (!atributos || typeof atributos !== 'object') return null;

  const pares = Object.entries(atributos)
    .filter(([, valor]) => valor !== null && valor !== undefined && String(valor).trim() !== '')
    .sort(([a], [b]) => a.localeCompare(b));

  return pares.length ? Object.fromEntries(pares.map(([k, v]) => [String(k), String(v)])) : null;
}

export function leerCarrito() {
  return leerCrudo()
    .map((item) => {
      const slug = String(item.slug ?? '').trim();
      const cantidad = normalizarCantidad(item.cantidad);
      const atributosSeleccionados = normalizarAtributos(item.atributosSeleccionados);
      const varianteId = item.varianteId ? String(item.varianteId) : null;

      if (!slug || cantidad <= 0) return null;

      return {
        itemKey: item.itemKey ?? generarItemKey(slug, varianteId, atributosSeleccionados),
        slug,
        cantidad,
        varianteId,
        atributosSeleccionados,
      };
    })
    .filter(Boolean);
}

export function contarItemsCarrito() {
  return leerCarrito().reduce((total, item) => total + item.cantidad, 0);
}

/**
 * @param {string} slug
 * @param {string|null} [varianteId]
 * @param {Record<string, string>|null} [atributos]
 */
export function generarItemKey(slug, varianteId = null, atributos = null) {
  if (varianteId) return `${slug}::var_${varianteId}`;

  const atributosNormalizados = normalizarAtributos(atributos);
  if (atributosNormalizados) {
    const attrStr = Object.entries(atributosNormalizados)
      .map(([clave, valor]) => `${clave}:${valor}`)
      .join(';');
    return `${slug}::attr_${attrStr}`;
  }

  return slug;
}

/**
 * @param {string} slug
 * @param {number} [cantidad]
 * @param {string|null} [varianteId]
 * @param {Record<string, string>|null} [atributosSeleccionados]
 */
export function agregarProducto(
  slug,
  cantidad = 1,
  varianteId = null,
  atributosSeleccionados = null,
) {
  const cantidadNormalizada = normalizarCantidad(cantidad);
  if (cantidadNormalizada <= 0) return;

  const atributos = normalizarAtributos(atributosSeleccionados);
  const itemKey = generarItemKey(slug, varianteId, atributos);
  const items = leerCarrito();
  const existente = items.find((item) => item.itemKey === itemKey);

  if (existente) {
    existente.cantidad += cantidadNormalizada;
  } else {
    items.push({
      itemKey,
      slug,
      cantidad: cantidadNormalizada,
      varianteId: varianteId ?? null,
      atributosSeleccionados: atributos,
    });
  }

  guardar(items);
}

/**
 * @param {string} itemKeyOrSlug
 * @param {number} cantidad
 */
export function actualizarCantidad(itemKeyOrSlug, cantidad) {
  const cantidadNormalizada = normalizarCantidad(cantidad);
  guardar(
    leerCarrito()
      .map((item) =>
        item.itemKey === itemKeyOrSlug ? { ...item, cantidad: cantidadNormalizada } : item,
      )
      .filter((item) => item.cantidad > 0),
  );
}

/** @param {string} itemKeyOrSlug */
export function quitarProducto(itemKeyOrSlug) {
  guardar(leerCarrito().filter((item) => item.itemKey !== itemKeyOrSlug));
}

export function vaciarCarrito() {
  guardar([]);
}

/**
 * @param {'vaciar'|'conservar'} decision
 */
export function aplicarDecisionPostEnvio(decision) {
  if (decision === 'vaciar') {
    vaciarCarrito();
    return;
  }

  guardar(leerCarrito());
}

function descripcionAtributos(atributos) {
  return Object.entries(atributos ?? {})
    .map(([clave, valor]) => `${clave}: ${valor}`)
    .join(', ');
}

/**
 * @param {object} variante
 */
function textoVariante(variante) {
  if (variante?.nombre) return variante.nombre;
  if (variante?.atributosSeleccionados)
    return descripcionAtributos(variante.atributosSeleccionados);
  if (Array.isArray(variante?.valores)) {
    return variante.valores
      .map(
        (valor) => `${valor.atributo_nombre ?? valor.atributo?.nombre ?? 'Opcion'}: ${valor.valor}`,
      )
      .join(', ');
  }
  return '';
}

/**
 * @param {object} tienda
 * @returns {Array<{ itemKey: string, slug: string, cantidad: number, producto: object, variante: object|null, varianteTexto: string, precio: number, subtotal: number }>}
 */
export function obtenerLineasCarrito(tienda) {
  const productos = tienda?.productos ?? [];
  const items = leerCarrito();

  const lineas = [];
  const obsoletos = [];

  for (const item of items) {
    const producto = productos.find((p) => p.slug === item.slug);
    if (!producto) {
      obsoletos.push(item);
      continue;
    }

    let variante = null;
    if (item.varianteId && Array.isArray(producto.variantes)) {
      variante = producto.variantes.find((v) => String(v.id) === String(item.varianteId)) ?? null;
    }

    const varianteTexto =
      textoVariante(variante) || descripcionAtributos(item.atributosSeleccionados);
    const precio = Number(variante?.precio ?? producto.precio ?? 0);

    lineas.push({
      itemKey: item.itemKey,
      slug: item.slug,
      cantidad: item.cantidad,
      varianteId: item.varianteId,
      atributosSeleccionados: item.atributosSeleccionados,
      producto,
      variante,
      varianteTexto,
      precio,
      subtotal: precio * item.cantidad,
    });
  }

  if (obsoletos.length) {
    guardar(items.filter((item) => !obsoletos.includes(item)));
  }

  return lineas;
}

/**
 * @param {object} tienda
 * @param {Array<object>} lineas
 * @param {{ nombre?: string, distrito?: string, observaciones?: string, telefono?: string }} [datosCliente]
 * @param {{ codigo?: string, descuento?: number } | null | undefined } [cupon]
 */
export function crearUrlWhatsApp(tienda, lineas, datosCliente = {}, cupon = null) {
  const telefono = String(tienda?.perfil?.whatsapp ?? tienda?.empresa?.whatsapp ?? '').replace(
    /\D/g,
    '',
  );
  if (!telefono || !lineas.length) return null;

  const moneda = tienda?.empresa?.moneda ?? 'PEN';
  const nombreEmpresa = tienda?.empresa?.nombre ?? 'la tienda';
  const totalGeneral = lineas.reduce((acc, item) => acc + item.subtotal, 0);

  const detalleProductos = lineas
    .map((linea, index) => {
      const variante = linea.varianteTexto ? `\n   Variante: ${linea.varianteTexto}` : '';
      return `${index + 1}. ${linea.cantidad} x ${linea.producto.nombre}${variante}\n   Precio unitario: ${formatearMoneda(linea.precio, moneda)}\n   Subtotal: ${formatearMoneda(linea.subtotal, moneda)}`;
    })
    .join('\n\n');

  const partes = [`Nuevo pedido para ${nombreEmpresa}`, ''];

  if (
    datosCliente?.nombre ||
    datosCliente?.telefono ||
    datosCliente?.distrito ||
    datosCliente?.observaciones
  ) {
    partes.push('Datos del cliente:');
    if (datosCliente.nombre) partes.push(`Nombre: ${datosCliente.nombre}`);
    if (datosCliente.telefono) partes.push(`Telefono: ${datosCliente.telefono}`);
    if (datosCliente.distrito) partes.push(`Distrito / Direccion: ${datosCliente.distrito}`);
    if (datosCliente.observaciones) partes.push(`Observaciones: ${datosCliente.observaciones}`);
    partes.push('');
  }

  partes.push('Detalle de productos:');
  partes.push(detalleProductos);
  partes.push('');

  if (cupon && cupon.descuento > 0) {
    partes.push(`Cupon aplicado: ${cupon.codigo}`);
    partes.push(`Descuento: -${formatearMoneda(cupon.descuento, moneda)}`);
    partes.push(
      `Total con descuento: ${formatearMoneda(Math.max(0, totalGeneral - cupon.descuento), moneda)}`,
    );
  } else {
    partes.push(`Total referencial: ${formatearMoneda(totalGeneral, moneda)}`);
  }

  partes.push('');
  partes.push('Mensaje generado desde la tienda online.');

  return `https://wa.me/${telefono}?text=${encodeURIComponent(partes.join('\n'))}`;
}

export function construirUrlFuncion(ruta, base = import.meta.env?.VITE_FUNCTIONS_BASE_URL) {
  if (!base) {
    throw new Error('Falta VITE_FUNCTIONS_BASE_URL en el entorno.');
  }
  return `${base}/functions/v1/${ruta}`;
}

function normalizarTelefono(telefono) {
  return String(telefono ?? '').replace(/\D/g, '');
}

function leerCupon() {
  try {
    const valor = JSON.parse(window.localStorage.getItem(CLAVE_CUPON) ?? 'null');
    return valor ?? null;
  } catch {
    return null;
  }
}

function guardarCupon(cupon) {
  if (typeof window !== 'undefined' && window.localStorage) {
    window.localStorage.setItem(CLAVE_CUPON, JSON.stringify(cupon));
  }
}

export function quitarCupon() {
  guardarCupon(null);
}

export function obtenerCuponAplicado() {
  return leerCupon();
}

export function calcularTotales(lineas, permiteCupones = true) {
  const subtotal = lineas.reduce((acc, l) => acc + Number(l.subtotal ?? 0), 0);
  let descuento = 0;
  let total = subtotal;

  if (permiteCupones) {
    const cupon = leerCupon();
    if (cupon) {
      descuento = Number(cupon.descuento ?? 0);
      total = Math.max(0, subtotal - descuento);
    }
  }

  return { subtotal, descuento, total };
}

export function mensajeErrorCupon(error) {
  const clave = String(error ?? '').toLowerCase();
  if (clave.includes('limite de usos') || clave.includes('usos_maximos'))
    return 'Este cupón ya no está disponible.';
  if (clave.includes('ya usaste')) return 'Ya alcanzaste el límite de uso de este cupón.';
  if (clave.includes('compra mínima'))
    return `Este cupón requiere una compra mínima de S/${Number(error.match(/[\d.]+/)?.[0] ?? 0).toFixed(2)}.`;
  if (clave.includes('no vigente') || clave.includes('fecha_inicio'))
    return 'Este cupón aún no está vigente.';
  if (clave.includes('vencido') || clave.includes('fecha_fin')) return 'Este cupón ya venció.';
  if (clave.includes('productos del carrito') || clave.includes('categorias del carrito'))
    return 'Este cupón no aplica a los productos de tu carrito.';
  if (clave.includes('inexistente') || clave.includes('inválido')) return 'Cupón no válido.';
  if (clave.includes('tienda no disponible')) return 'Tienda no disponible.';
  if (clave.includes('datos insuficientes') || clave.includes('faltan campos'))
    return 'Datos insuficientes para validar el cupón.';
  if (clave.includes('error de conexion')) return 'Error de conexión al validar el cupón.';
  if (clave.includes('error interno')) return 'Error interno del servidor. Intenta nuevamente.';
  if (clave.includes('ya hay un cupon'))
    return 'Ya tienes un cupón aplicado. Quítalo antes de aplicar otro.';
  if (clave.includes('no hay cupon aplicado')) return 'No hay cupón aplicado para confirmar.';
  if (clave.includes('no se pudo aplicar')) return 'No se pudo confirmar el cupón en el pedido.';
  if (clave.includes('carrito esta vacio')) return 'El carrito está vacío.';
  if (clave.includes('configuracion de plataforma'))
    return 'Configuración de plataforma no disponible.';
  return 'Cupón no válido.';
}

export async function aplicarCupon(tienda, codigo, telefonoCliente = '') {
  const cuponExistente = leerCupon();
  if (cuponExistente) {
    return { error: 'Ya hay un cupon aplicado. Quitalo antes de aplicar otro.' };
  }

  const lineas = obtenerLineasCarrito(tienda);
  if (!lineas.length) {
    return { error: 'El carrito esta vacio' };
  }

  const url = construirUrlFuncion('validar_cupon');

  const payload = {
    codigo,
    slug_tienda: tienda.empresa.slug,
    lineas: lineas.map((l) => ({
      producto_id: l.producto.id,
      variante_id: l.varianteId ?? null,
      cantidad: l.cantidad,
      subtotal: l.subtotal,
      categoria_id: l.producto.categorias?.find((c) => c.es_principal)?.categoria_id ?? null,
    })),
    telefono_cliente: telefonoCliente ? normalizarTelefono(telefonoCliente) : '',
  };

  try {
    const respuesta = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });

    const resultado = await respuesta.json();
    if (!respuesta.ok || resultado.valido === false) {
      return { error: resultado.error || 'Cupon invalido' };
    }

    guardarCupon({
      codigo: resultado.codigo,
      descuento: resultado.descuento,
      subtotal: resultado.subtotal,
      total: resultado.total,
      tipo: resultado.tipo,
      valor: resultado.valor,
      combinable: resultado.combinable ?? false,
      telefonoCliente: normalizarTelefono(telefonoCliente),
    });

    window.dispatchEvent(new CustomEvent(EVENTO_CAMBIO));

    return resultado;
  } catch {
    return { error: 'Error de conexion al validar cupon' };
  }
}

export async function consumirCupon(tienda, telefonoCliente = '') {
  const cupon = obtenerCuponAplicado();
  if (!cupon) {
    return { error: 'No hay cupon aplicado para consumir.' };
  }

  const lineas = obtenerLineasCarrito(tienda);
  if (!lineas.length) {
    return { error: 'El carrito esta vacio' };
  }

  const url = construirUrlFuncion('consumir_cupon');

  const payload = {
    codigo: cupon.codigo,
    slug_tienda: tienda.empresa.slug,
    lineas: lineas.map((l) => ({
      producto_id: l.producto.id,
      variante_id: l.varianteId ?? null,
      cantidad: l.cantidad,
      subtotal: l.subtotal,
      categoria_id: l.producto.categorias?.find((c) => c.es_principal)?.categoria_id ?? null,
    })),
    telefono_cliente: telefonoCliente ? normalizarTelefono(telefonoCliente) : '',
  };

  try {
    const respuesta = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });

    const resultado = await respuesta.json();
    if (!respuesta.ok || resultado.valido === false) {
      return { error: resultado.error || 'No se pudo aplicar el cupon al pedido' };
    }

    quitarCupon();
    return resultado;
  } catch {
    return { error: 'Error de conexion al consumir cupon' };
  }
}

export function onCambioCarrito(callback) {
  if (typeof window !== 'undefined') {
    window.addEventListener(EVENTO_CAMBIO, callback);
  }
}
